import { FocusScope } from '@react-aria/focus';
import { useEffect, useRef, useState } from 'react';

interface SlidePanelProps {
    isOpen: boolean;
    isSidePanel?: boolean;
    height?: number;
    width?: number;
    onClose: () => void;
    children: React.ReactNode;
    title: string;  // Required for accessibility
    description?: string;  // Optional description for more context
}

function SlidePanel({ 
    isOpen, 
    isSidePanel, 
    height, 
    width, 
    onClose, 
    children,
    title,
    description 
}: SlidePanelProps) {
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useRef(`panel-${Math.random().toString(36).substr(2, 9)}`).current;
    const descriptionId = useRef(`panel-desc-${Math.random().toString(36).substr(2, 9)}`).current;
    
    // touch controls for mobile devices
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const minSwipeDistance = 50;

    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(isSidePanel ? e.touches[0].clientX : e.touches[0].clientY);
    };
      
    const onTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(isSidePanel ? e.touches[0].clientX : e.touches[0].clientY);
    };
      
    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = isSidePanel && (distance > minSwipeDistance);
        const isUpSwipe = !isSidePanel && (distance > minSwipeDistance);
        if (isLeftSwipe || isUpSwipe) {
            onClose();
        }
    };

    useEffect(() => {
        // Handle escape key
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
                // Announce panel closure to screen readers
                const announcement = document.createElement('div');
                announcement.setAttribute('role', 'status');
                announcement.setAttribute('aria-live', 'polite');
                announcement.textContent = 'Panel closed';
                document.body.appendChild(announcement);
                setTimeout(() => document.body.removeChild(announcement), 1000);
            }
        };
        const controller = new AbortController()
        const signal = controller.signal
        document.addEventListener('keydown', handleEscape, { signal });

        // Announce panel opening to screen readers
        const announcement = document.createElement('div');
        announcement.classList.add('sr-only')
        announcement.setAttribute('role', 'status');
        announcement.setAttribute('aria-live', 'polite');
        announcement.textContent = `${title} panel opened`;
        document.body.appendChild(announcement);
        setTimeout(() => document.body.removeChild(announcement), 1000);

        // Restore focus when panel closes
        return () => {
            controller.abort()
        };
    }, [isOpen, onClose, title]);

    // Stop propagation of clicks within the panel
    const handlePanelClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    return (
        <div
            className={`
                absolute inset-0 z-50
                ${isOpen ? 'pointer-events-auto' : 'pointer-events-none'}
            `}
            role="presentation"
            aria-hidden={!isOpen}
        >
            {/* Backdrop */}
            <div
                className={`
                    absolute inset-0 
                    bg-black
                    transition-opacity duration-300 ease-in-out
                    ${isOpen ? 'opacity-50' : 'opacity-0'}
                `}
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Panel */}
            <div
                style={{width: width ?? 'auto', height: height ?? 'auto'}}
                className={`
                    ${isSidePanel ? "top-0 left-0 bottom-0" : "top-0 left-0 right-0"}
                    absolute transform transition-transform duration-300 ease-in-out
                    bg-white shadow-lg
                    ${isOpen ? isSidePanel ? 'translate-x-0' : 'translate-y-0' : isSidePanel ? '-translate-x-full' : '-translate-y-full'}
                `}
                ref={panelRef}
                onClick={handlePanelClick}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={description ? descriptionId : undefined}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                <div className={`transition-opacity duration-500 ease-in-out ${isOpen ? 'opacity-100' : 'opacity-0'}`}>
                {isOpen && (
                    <FocusScope contain restoreFocus autoFocus>
                        <h2 id={titleId} className="sr-only">
                            {title}
                        </h2>
                        {description && (
                            <div id={descriptionId} className="sr-only">
                                {description}
                            </div>
                        )}
                        {children}
                    </FocusScope>
                )}
                </div>
                
            </div>
        </div>
    );
}

export default SlidePanel;